import { useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useNetwork } from './NetworkContext';
import API_URL from './config';

const DEFAULT_TIMEOUT = 20000; // 20 seconds — avoids false timeouts on slow mobile
const SLOW_TIMEOUT = 25000;   // 25 seconds for score submissions etc.

export const useApi = () => {
  const { currentUser, currentSociety } = useAuth();
  const { reportRequestOutcome } = useNetwork();

  const authenticatedFetch = useCallback(async (endpoint, options = {}) => {
    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const token = await currentUser.getIdToken();
    const headers = options.headers || {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (currentSociety && !headers['X-Society-ID']) {
      headers['X-Society-ID'] = currentSociety.id;
    }

    // Set up AbortController for timeout
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    const controller = new AbortController();
    // Merge with any existing signal
    const existingSignal = options.signal;
    if (existingSignal) {
      existingSignal.addEventListener('abort', () => controller.abort());
    }
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const { timeout: _t, signal: _s, ...fetchOptions } = options;
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      reportRequestOutcome(response.ok);
      return response;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        reportRequestOutcome(false);
        throw new Error(`Request timed out after ${timeout / 1000}s`);
      }
      reportRequestOutcome(false);
      throw err;
    }
  }, [currentUser, currentSociety, reportRequestOutcome]);

  return { authenticatedFetch };
};

export { DEFAULT_TIMEOUT, SLOW_TIMEOUT };
